import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { RefreshCw, ArrowDownUp, Wallet, TrendingUp, Clock, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { mm2Service, Orderbook, OrderbookEntry, SwapStatus } from '../../services/mm2Service';

interface CoinBalance {
  coin: string;
  balance: string;
  address?: string;
}

const KomodoDEXInterface: React.FC = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [balances, setBalances] = useState<CoinBalance[]>([]);
  const [orderbook, setOrderbook] = useState<Orderbook | null>(null);
  const [myOrders, setMyOrders] = useState<any[]>([]);
  const [recentSwaps, setRecentSwaps] = useState<SwapStatus[]>([]);

  // Trading state
  const [baseCoin, setBaseCoin] = useState('WTX');
  const [relCoin, setRelCoin] = useState('BTC');
  const [tradeType, setTradeType] = useState<'buy' | 'sell'>('buy');
  const [amount, setAmount] = useState('');
  const [price, setPrice] = useState('');

  // Available coins
  const availableCoins = ['WTX', 'HTH', 'BTC', 'LTC'];

  // Check connection status
  const checkConnection = useCallback(async () => {
    try {
      const ready = await mm2Service.isReady();
      setIsConnected(ready);

      if (ready) {
        await loadBalances();
        await loadOrderbook();
        await loadMyOrders();
        await loadRecentSwaps();
      }
    } catch (error) {
      console.error('Connection check failed:', error);
      setIsConnected(false);
    }
  }, [baseCoin, relCoin]);

  // Initialize on mount
  useEffect(() => {
    checkConnection();
    const interval = setInterval(checkConnection, 30000);
    return () => clearInterval(interval);
  }, [checkConnection]);

  // Load balances for enabled coins
  const loadBalances = async () => {
    try {
      const enabledCoins = await mm2Service.getEnabledCoins();
      const balancePromises = enabledCoins.map(async (coin) => {
        try {
          const balance = await mm2Service.getBalance(coin);
          return { coin, balance: balance.balance };
        } catch {
          return { coin, balance: '0' };
        }
      });

      const results = await Promise.all(balancePromises);
      setBalances(results);
    } catch (error) {
      console.error('Failed to load balances:', error);
    }
  };

  // Load orderbook
  const loadOrderbook = async () => {
    try {
      const ob = await mm2Service.getOrderbook(baseCoin, relCoin);
      setOrderbook(ob);
    } catch (error) {
      console.error('Failed to load orderbook:', error);
      setOrderbook(null);
    }
  };

  // Load my orders
  const loadMyOrders = async () => {
    try {
      const orders = await mm2Service.getMyOrders();
      const allOrders = [
        ...Object.values(orders.maker_orders || {}),
        ...Object.values(orders.taker_orders || {})
      ];
      setMyOrders(allOrders);
    } catch (error) {
      console.error('Failed to load orders:', error);
    }
  };

  // Load recent swaps
  const loadRecentSwaps = async () => {
    try {
      const swaps = await mm2Service.getRecentSwaps(10);
      setRecentSwaps(swaps);
    } catch (error) {
      console.error('Failed to load swaps:', error);
    }
  };

  // Initialize coins
  const initializeCoins = async () => {
    setIsLoading(true);
    toast.loading('Initializing coins...', { id: 'init' });

    try {
      await mm2Service.initializeDefaultCoins();
      await loadBalances();
      toast.success('Coins initialized!', { id: 'init' });
    } catch (error) {
      toast.error('Failed to initialize coins', { id: 'init' });
    } finally {
      setIsLoading(false);
    }
  };

  // Place order
  const placeOrder = async () => {
    if (!amount || !price) {
      toast.error('Please enter amount and price');
      return;
    }

    setIsLoading(true);
    toast.loading('Placing order...', { id: 'order' });

    try {
      let result;
      if (tradeType === 'buy') {
        result = await mm2Service.buy(baseCoin, relCoin, price, amount);
      } else {
        result = await mm2Service.sell(baseCoin, relCoin, price, amount);
      }

      toast.success(`Order placed! UUID: ${result.uuid.slice(0, 8)}...`, { id: 'order' });
      setAmount('');
      setPrice('');
      await loadMyOrders();
    } catch (error: any) {
      toast.error(error.message || 'Failed to place order', { id: 'order' });
    } finally {
      setIsLoading(false);
    }
  };

  // Cancel order
  const cancelOrder = async (uuid: string) => {
    try {
      await mm2Service.cancelOrder(uuid);
      toast.success('Order cancelled');
      await loadMyOrders();
    } catch (error) {
      toast.error('Failed to cancel order');
    }
  };

  // Swap coins
  const swapCoins = () => {
    setBaseCoin(relCoin);
    setRelCoin(baseCoin);
    setTradeType(tradeType === 'buy' ? 'sell' : 'buy');
  };

  // Format price for display
  const formatPrice = (priceStr: string) => {
    const p = parseFloat(priceStr);
    if (p < 0.00001) return p.toExponential(4);
    if (p < 1) return p.toFixed(8);
    return p.toFixed(4);
  };

  return (
    <div className="space-y-6">
      {/* Connection Status */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between bg-gray-900/50 rounded-xl p-4 border border-gray-800"
      >
        <div className="flex items-center space-x-3">
          <div className={`w-3 h-3 rounded-full ${isConnected ? 'bg-emerald-400' : 'bg-red-400'} animate-pulse`} />
          <span className="text-gray-300">
            Komodo DEX {isConnected ? 'Connected' : 'Disconnected'}
          </span>
        </div>

        <div className="flex items-center space-x-2">
          <button
            onClick={initializeCoins}
            disabled={isLoading}
            className="px-4 py-2 bg-yellow-600 hover:bg-yellow-500 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
          >
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Initialize Coins'}
          </button>
          <button
            onClick={checkConnection}
            className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </motion.div>

      {/* Balances */}
      {balances.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4"
        >
          {balances.map((b) => (
            <div key={b.coin} className="bg-gray-900/50 rounded-xl p-4 border border-gray-800">
              <div className="flex items-center space-x-2 text-gray-400 mb-1">
                <Wallet className="w-4 h-4" />
                <span className="text-sm">{b.coin}</span>
              </div>
              <p className="text-lg font-bold text-white">{parseFloat(b.balance).toFixed(8)}</p>
            </div>
          ))}
        </motion.div>
      )}

      {/* Trading Interface */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Order Form */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-gray-900/50 rounded-xl p-6 border border-gray-800"
        >
          <h3 className="text-lg font-semibold mb-4 flex items-center space-x-2">
            <TrendingUp className="w-5 h-5 text-yellow-400" />
            <span>Atomic Swap</span>
          </h3>

          {/* Pair Selector */}
          <div className="flex items-center space-x-2 mb-4">
            <select
              value={baseCoin}
              onChange={(e) => setBaseCoin(e.target.value)}
              className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white"
            >
              {availableCoins.map((coin) => (
                <option key={coin} value={coin}>{coin}</option>
              ))}
            </select>

            <button
              onClick={swapCoins}
              className="p-3 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
            >
              <ArrowDownUp className="w-5 h-5" />
            </button>

            <select
              value={relCoin}
              onChange={(e) => setRelCoin(e.target.value)}
              className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white"
            >
              {availableCoins.filter(c => c !== baseCoin).map((coin) => (
                <option key={coin} value={coin}>{coin}</option>
              ))}
            </select>
          </div>

          {/* Trade Type */}
          <div className="flex space-x-2 mb-4">
            <button
              onClick={() => setTradeType('buy')}
              className={`flex-1 py-2 rounded-lg font-medium transition-colors ${
                tradeType === 'buy'
                  ? 'bg-emerald-600 text-white'
                  : 'bg-gray-800 text-gray-400'
              }`}
            >
              Buy {baseCoin}
            </button>
            <button
              onClick={() => setTradeType('sell')}
              className={`flex-1 py-2 rounded-lg font-medium transition-colors ${
                tradeType === 'sell'
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-800 text-gray-400'
              }`}
            >
              Sell {baseCoin}
            </button>
          </div>

          {/* Amount & Price */}
          <div className="space-y-3">
            <div>
              <label className="text-sm text-gray-400">Amount ({baseCoin})</label>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white mt-1"
              />
            </div>
            <div>
              <label className="text-sm text-gray-400">Price ({relCoin} per {baseCoin})</label>
              <input
                type="number"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="0.00000000"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white mt-1"
              />
            </div>

            {amount && price && (
              <div className="text-sm text-gray-400">
                Total: {(parseFloat(amount) * parseFloat(price)).toFixed(8)} {relCoin}
              </div>
            )}
          </div>

          <button
            onClick={placeOrder}
            disabled={isLoading || !amount || !price}
            className={`w-full py-3 rounded-lg font-semibold mt-4 transition-colors ${
              tradeType === 'buy'
                ? 'bg-emerald-600 hover:bg-emerald-500'
                : 'bg-red-600 hover:bg-red-500'
            } text-white disabled:opacity-50`}
          >
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin mx-auto" />
            ) : (
              `${tradeType === 'buy' ? 'Buy' : 'Sell'} ${baseCoin}`
            )}
          </button>
        </motion.div>

        {/* Orderbook */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-gray-900/50 rounded-xl p-6 border border-gray-800"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">
              Orderbook: {baseCoin}/{relCoin}
            </h3>
            <button onClick={loadOrderbook} className="p-2 hover:bg-gray-800 rounded-lg">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>

          {orderbook ? (
            <div className="space-y-4">
              {/* Asks (Sells) */}
              <div>
                <p className="text-sm text-gray-400 mb-2">Asks (Sell Orders)</p>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {orderbook.asks.slice(0, 5).map((ask, i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <span className="text-red-400">{formatPrice(ask.price)}</span>
                      <span className="text-gray-300">{parseFloat(ask.maxvolume).toFixed(4)}</span>
                    </div>
                  ))}
                  {orderbook.asks.length === 0 && (
                    <p className="text-gray-500 text-sm">No asks</p>
                  )}
                </div>
              </div>

              <div className="border-t border-gray-700 my-2" />

              {/* Bids (Buys) */}
              <div>
                <p className="text-sm text-gray-400 mb-2">Bids (Buy Orders)</p>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {orderbook.bids.slice(0, 5).map((bid, i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <span className="text-emerald-400">{formatPrice(bid.price)}</span>
                      <span className="text-gray-300">{parseFloat(bid.maxvolume).toFixed(4)}</span>
                    </div>
                  ))}
                  {orderbook.bids.length === 0 && (
                    <p className="text-gray-500 text-sm">No bids</p>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p>No orderbook data available</p>
              <p className="text-sm mt-1">Try initializing coins first</p>
            </div>
          )}
        </motion.div>
      </div>

      {/* My Orders */}
      {myOrders.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gray-900/50 rounded-xl p-6 border border-gray-800"
        >
          <h3 className="text-lg font-semibold mb-4 flex items-center space-x-2">
            <Clock className="w-5 h-5 text-blue-400" />
            <span>My Orders</span>
          </h3>

          <div className="space-y-2">
            {myOrders.map((order: any, i) => (
              <div key={i} className="flex items-center justify-between bg-gray-800/50 rounded-lg p-3">
                <div>
                  <span className="font-medium">{order.base}/{order.rel}</span>
                  <span className="text-sm text-gray-400 ml-2">
                    {order.max_base_vol} @ {order.price}
                  </span>
                </div>
                <button
                  onClick={() => cancelOrder(order.uuid)}
                  className="px-3 py-1 bg-red-600/20 text-red-400 rounded hover:bg-red-600/30 transition-colors"
                >
                  Cancel
                </button>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Recent Swaps */}
      {recentSwaps.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gray-900/50 rounded-xl p-6 border border-gray-800"
        >
          <h3 className="text-lg font-semibold mb-4 flex items-center space-x-2">
            <ArrowDownUp className="w-5 h-5 text-purple-400" />
            <span>Recent Swaps</span>
          </h3>

          <div className="space-y-2">
            {recentSwaps.map((swap, i) => (
              <div key={i} className="flex items-center justify-between bg-gray-800/50 rounded-lg p-3">
                <div className="flex items-center space-x-3">
                  {swap.events.some(e => e.event.type === 'Finished') ? (
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                  ) : swap.events.some(e => e.event.type.includes('Error')) ? (
                    <XCircle className="w-5 h-5 text-red-400" />
                  ) : (
                    <Loader2 className="w-5 h-5 text-yellow-400 animate-spin" />
                  )}
                  <div>
                    <span className="font-medium">
                      {swap.my_info?.my_coin} → {swap.my_info?.other_coin}
                    </span>
                    <p className="text-sm text-gray-400">
                      {swap.my_info?.my_amount} {swap.my_info?.my_coin}
                    </p>
                  </div>
                </div>
                <span className="text-xs text-gray-500">
                  {swap.uuid.slice(0, 8)}...
                </span>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default KomodoDEXInterface;
